import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toShortCode } from "./useLanguage.js";

const OG_LOCALE = { en: "en_US", el: "el_GR" };

const upsertMeta = (selector, attr, name, content) => {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

/**
 * Set `<link rel="canonical">`, creating the tag when the document has none.
 * Returns the href that was there before, so the caller can put it back on
 * unmount — a client-side navigation away must not leave another route's
 * canonical in the head.
 */
const setCanonical = (href) => {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  const previous = tag.getAttribute("href");
  tag.setAttribute("href", href);
  return previous;
};

/**
 * Replace the `<link rel="alternate" hreflang>` set, returning the one that was
 * there so the caller can put it back on unmount — symmetric with
 * `setCanonical`, and for the same reason: an SPA navigation must not leave one
 * route's alternates advertising another route's translations.
 *
 * The set is replaced wholesale rather than patched. hreflang is only ever
 * correct as a complete, reciprocal group, so a half-updated set is worse than
 * either the old one or the new one.
 */
const setAlternates = (alternates) => {
  const existing = [
    ...document.querySelectorAll('link[rel="alternate"][hreflang]'),
  ];
  const previous = existing.map((tag) => ({
    hreflang: tag.getAttribute("hreflang"),
    href: tag.getAttribute("href"),
  }));

  existing.forEach((tag) => tag.remove());
  for (const alternate of alternates) {
    const tag = document.createElement("link");
    tag.setAttribute("rel", "alternate");
    tag.setAttribute("hreflang", alternate.hreflang);
    tag.setAttribute("href", alternate.href);
    document.head.appendChild(tag);
  }
  return previous;
};

/**
 * Drive the document head for a route.
 *
 * `titleKey` / `descriptionKey` are i18n keys, so the head follows the language
 * toggle. `canonical` and `robots` are literal values (a canonical URL is not
 * translated) and are OPTIONAL: pages that don't pass them keep whatever
 * index.html declared, which is the pre-existing behaviour for every page that
 * predates them. Both are restored on unmount so an SPA navigation can't leave
 * one route's canonical or robots directive on another route.
 *
 * `title` / `description` are literal alternatives to the two key parameters,
 * and win when both are given. They exist for /guides/:slug, where the head
 * copy belongs to the guide's own content module — which already knows the
 * language it is written in — rather than to a shared `meta.json` namespace.
 * Every page that predates them keeps passing keys and is unaffected.
 *
 * `alternates` is the hreflang set, built by `alternatesFor` in the page
 * registry — which returns an empty array unless the page genuinely exists in
 * more than one language, so a page with no counterpart emits no alternates at
 * all rather than a set naming only itself. Like `canonical`, it is OPTIONAL
 * and restored on unmount.
 *
 * `og:url` follows `canonical`. Both are the page's own URL, so a localized
 * page's social card points at the localized page; until now `og:url` existed
 * only in the static documents and kept the entry page's value for the rest of
 * an SPA session.
 */
export default function useDocumentMeta({
  titleKey,
  descriptionKey,
  title: titleLiteral,
  description: descriptionLiteral,
  canonical,
  robots,
  alternates,
} = {}) {
  const { t, i18n } = useTranslation();

  // Serialised so an inline array literal at the call site does not re-run the
  // effect on every render. The list is read back out of the key inside the
  // effect, which keeps the dependency honest rather than suppressed.
  const alternatesKey = alternates?.length ? JSON.stringify(alternates) : "";

  useEffect(() => {
    if (!canonical && !robots && !alternatesKey) return undefined;

    const previousCanonical = canonical ? setCanonical(canonical) : null;

    const ogUrlTag = canonical
      ? document.querySelector('meta[property="og:url"]')
      : null;
    const previousOgUrl = ogUrlTag?.getAttribute("content") ?? null;
    if (canonical) {
      upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }

    const robotsTag = robots
      ? document.querySelector('meta[name="robots"]')
      : null;
    const previousRobots = robotsTag?.getAttribute("content") ?? null;
    if (robots) {
      upsertMeta('meta[name="robots"]', "name", "robots", robots);
    }

    const previousAlternates = alternatesKey
      ? setAlternates(JSON.parse(alternatesKey))
      : null;

    return () => {
      if (canonical && previousCanonical) setCanonical(previousCanonical);
      if (canonical && previousOgUrl) {
        upsertMeta('meta[property="og:url"]', "property", "og:url", previousOgUrl);
      }
      if (robots && previousRobots) {
        upsertMeta('meta[name="robots"]', "name", "robots", previousRobots);
      }
      if (previousAlternates) setAlternates(previousAlternates);
    };
  }, [canonical, robots, alternatesKey]);

  useEffect(() => {
    const lang = toShortCode(i18n.resolvedLanguage || i18n.language);
    document.documentElement.lang = lang;

    const title =
      titleLiteral ||
      (titleKey && t(titleKey) !== titleKey ? t(titleKey) : null);
    const description =
      descriptionLiteral ||
      (descriptionKey && t(descriptionKey) !== descriptionKey
        ? t(descriptionKey)
        : null);

    if (title) {
      document.title = title;
      upsertMeta('meta[property="og:title"]', "property", "og:title", title);
      upsertMeta(
        'meta[property="twitter:title"]',
        "property",
        "twitter:title",
        title,
      );
    }

    if (description) {
      upsertMeta(
        'meta[name="description"]',
        "name",
        "description",
        description,
      );
      upsertMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        description,
      );
      upsertMeta(
        'meta[property="twitter:description"]',
        "property",
        "twitter:description",
        description,
      );
    }

    upsertMeta(
      'meta[property="og:locale"]',
      "property",
      "og:locale",
      OG_LOCALE[lang] || OG_LOCALE.en,
    );
  }, [
    t,
    i18n.resolvedLanguage,
    i18n.language,
    titleKey,
    descriptionKey,
    titleLiteral,
    descriptionLiteral,
  ]);
}
